import { TopIssueCard } from 'components/Cards/TopIssueCard';
import { formatNumberAsCurrency, getPathWithRef, getUrlParams } from 'helpers/utils';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch } from 'helpers/hooks';
import { getRecipientsAPI, getRecipients } from 'store/recipients/actions';
import { IRecipient, RecipientsRequestType } from 'store/recipients/types';
import { IPage } from 'store/donatePage/types';
import { getDonatePage } from 'store/donatePage/actions';
import { ProcessingRedirectOptions } from 'store/donatePage/constants';
import { OathRecurringInterval } from 'store/pay/types';
import { getStripeExpressCheckoutUrl } from 'helpers/donate';

interface Props {
  donationAmount: number;
  issue: IPage;
  recurringInterval: OathRecurringInterval | 'one_time';
}

export const StripeExpressCard: FC<Props> = ({ donationAmount, issue, recurringInterval }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { urlPartner } = getUrlParams();
  const [recipients, setRecipients] = useState([] as IRecipient[]);
  const [hasError, setHasError] = useState(false);
  const recipientCount = issue.recipient_ids?.length || 0;
  const donationAmountAdjusted = recipientCount > donationAmount ? recipientCount : donationAmount;
  const requestLocationPath = location.pathname;

  useEffect(() => {
    if (recipients.length) {
      return;
    }
    getRecipientsAPI({
      amount: 200,
      recipient_ids: issue.recipient_ids || [],
      payment_processor: ProcessingRedirectOptions.STRIPE,
      page_query_str: urlPartner,
      request_type: RecipientsRequestType.checkout,
      request_location_path: requestLocationPath,
    }).then(response => {
      setRecipients(response.data.recipients);
    });
  }, [issue.query_str, recipients.length]);

  const handleDonate = async () => {
    if (recipients.length === 0) {
      setHasError(true);
      return;
    }

    setHasError(false);

    try {
      await dispatch(getDonatePage({ urlPartner: issue.query_str }));

      dispatch(
        getRecipients({
          amount: 200,
          recipient_ids: recipients.map(r => r.recipient_id) || [],
          payment_processor: ProcessingRedirectOptions.STRIPE,
          request_location_path: requestLocationPath,
          request_type: RecipientsRequestType.checkout,
        })
      );

      navigate(
        getStripeExpressCheckoutUrl({
          page: issue,
          donationAmount: donationAmountAdjusted,
          recurringInterval,
        })
      );
    } catch {
      setHasError(true);
    }
  };

  return (
    <div className="donate-again-card-container" data-testid="donate-again-card">
      <TopIssueCard issue={issue} fullHeight={true}>
        <>
          <button
            className={`button-brand mt16 ${hasError ? 'mb7 bg-errorRed' : 'mb16'}`}
            onClick={handleDonate}
          >
            {hasError
              ? `Try Again · ${formatNumberAsCurrency(donationAmountAdjusted)}`
              : `Express Donate ${formatNumberAsCurrency(donationAmountAdjusted)}`}
          </button>
          {hasError && (
            <div className="mb16 full-width text-center">
              <span className="body-small-text color-errorRed">
                Something went wrong. Please try again.
              </span>
            </div>
          )}
          <div className="top-issue-line" />
          <div className="eyebrow-text color-inkBlue mb6">Recipients</div>
          <div className="recipients-container mb25">
            {recipients.map(rec => (
              <div className="recipient" key={rec.recipient_id}>
                <span className="body-text">{rec.recipient_name}</span>
              </div>
            ))}
          </div>
          <div className="donate-again-card-learn-more">
            <a
              className="link-text"
              href={getPathWithRef(issue.learn_more_link || `/donate?p=${issue.query_str}`)}
              target="_blank"
              rel="noreferrer"
            >
              <span className="body-small-text">Learn More</span>
            </a>
          </div>
        </>
      </TopIssueCard>
    </div>
  );
};
